import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { CheckCircle2, XCircle, FileText, Image, ExternalLink, Eye, ZoomIn, Clock, AlertCircle, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JobsheetReviewPage() {
  const { user, hasRole } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // For Super Admin: 'all', 'pending', 'submitted', 'approved', 'rejected'
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  
  const isSuperAdmin = hasRole('Super Admin');
  
  // Rejection form state
  const [rejectJobsheet, setRejectJobsheet] = useState(false);
  const [rejectBeforePhoto, setRejectBeforePhoto] = useState(false);
  const [rejectAfterPhoto, setRejectAfterPhoto] = useState(false);
  const [jobsheetRemarks, setJobsheetRemarks] = useState('');
  const [beforePhotoRemarks, setBeforePhotoRemarks] = useState('');
  const [afterPhotoRemarks, setAfterPhotoRemarks] = useState('');

  useEffect(() => {
    fetchQuotes();
  }, [isSuperAdmin]);

  const fetchQuotes = async () => {
    try {
      // Super Admin sees all jobsheet-related quotes, Billing Manager sees only submitted ones
      const endpoint = isSuperAdmin ? `${API}/quotes/jobsheet-all` : `${API}/quotes/jobsheet-pending`;
      const response = await axios.get(endpoint);
      
      // Fetch presigned URLs for each quote's files
      const quotesWithUrls = await Promise.all(
        response.data.map(async (quote) => {
          const urls = {};
          
          // Get presigned URLs for each file
          if (quote.jobsheet_path) {
            try {
              const res = await axios.get(`${API}/files/url/${encodeURIComponent(quote.jobsheet_path)}`);
              urls.jobsheet_url = res.data.url;
            } catch (e) {
              console.error('Error getting jobsheet URL:', e);
            }
          }
          if (quote.before_photo_path) {
            try {
              const res = await axios.get(`${API}/files/url/${encodeURIComponent(quote.before_photo_path)}`);
              urls.before_photo_url = res.data.url;
            } catch (e) {
              console.error('Error getting before photo URL:', e);
            }
          }
          if (quote.after_photo_path) {
            try {
              const res = await axios.get(`${API}/files/url/${encodeURIComponent(quote.after_photo_path)}`);
              urls.after_photo_url = res.data.url;
            } catch (e) {
              console.error('Error getting after photo URL:', e);
            }
          }
          
          return { ...quote, ...urls };
        })
      );
      
      setQuotes(quotesWithUrls);
    } catch (error) {
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const openPreview = (url, title) => {
    if (!url) {
      toast.error('File not available');
      return;
    }
    setPreviewUrl(url);
    setPreviewTitle(title);
    setPreviewDialogOpen(true);
  };

  const isImageFile = (path) => {
    if (!path) return false;
    const ext = path.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  };

  const handleApprove = async (quote) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes/${quote.id}/billing-approve`);
      toast.success('Jobsheet approved. Quote completed.');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  const openRejectDialog = (quote) => {
    setSelectedQuote(quote);
    setRejectJobsheet(false);
    setRejectBeforePhoto(false);
    setRejectAfterPhoto(false);
    setJobsheetRemarks('');
    setBeforePhotoRemarks('');
    setAfterPhotoRemarks('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!rejectJobsheet && !rejectBeforePhoto && !rejectAfterPhoto) {
      toast.error('Please select at least one item to reject');
      return;
    }
    
    if (rejectJobsheet && !jobsheetRemarks.trim()) {
      toast.error('Remarks are mandatory for Job Sheet rejection');
      return;
    }
    if (rejectBeforePhoto && !beforePhotoRemarks.trim()) {
      toast.error('Remarks are mandatory for Before Photo rejection');
      return;
    }
    if (rejectAfterPhoto && !afterPhotoRemarks.trim()) {
      toast.error('Remarks are mandatory for After Photo rejection');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes/${selectedQuote.id}/billing-reject`, {
        reject_jobsheet: rejectJobsheet,
        reject_before_photo: rejectBeforePhoto,
        reject_after_photo: rejectAfterPhoto,
        jobsheet_remarks: rejectJobsheet ? jobsheetRemarks : null,
        before_photo_remarks: rejectBeforePhoto ? beforePhotoRemarks : null,
        after_photo_remarks: rejectAfterPhoto ? afterPhotoRemarks : null
      });
      toast.success('Jobsheet rejected. Workflow assigned back to Site Supervisor.');
      setRejectDialogOpen(false);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  const getFileUrl = async (path) => {
    if (!path) return null;
    try {
      // Get presigned URL from backend
      const response = await axios.get(`${API}/files/url/${encodeURIComponent(path)}`);
      return response.data.url;
    } catch (error) {
      console.error('Error getting file URL:', error);
      return null;
    }
  };

  const openPreviewAsync = async (path, title) => {
    if (!path) {
      toast.error('No file available');
      return;
    }
    setPreviewTitle(title);
    setPreviewDialogOpen(true);
    setPreviewUrl(null); // Show loading state
    
    try {
      const response = await axios.get(`${API}/files/url/${encodeURIComponent(path)}`);
      setPreviewUrl(response.data.url);
    } catch (error) {
      toast.error('Failed to load file preview');
      setPreviewDialogOpen(false);
    }
  };

  const openFileInNewTab = async (path) => {
    if (!path) {
      toast.error('No file available');
      return;
    }
    try {
      const response = await axios.get(`${API}/files/url/${encodeURIComponent(path)}`);
      window.open(response.data.url, '_blank');
    } catch (error) {
      toast.error('Failed to open file');
    }
  };

  const getStatusBadge = (workStatus) => {
    switch (workStatus) {
      case 'jobsheet_pending':
        return (
          <Badge className="bg-amber-100 text-amber-800">
            <Clock className="h-3 w-3 mr-1" />
            Awaiting Upload
          </Badge>
        );
      case 'jobsheet_submitted':
        return (
          <Badge className="bg-purple-100 text-purple-800">
            <FileText className="h-3 w-3 mr-1" />
            Pending Review
          </Badge>
        );
      case 'billing_approved':
        return (
          <Badge className="bg-green-100 text-green-800">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        );
      case 'billing_rejected':
        return (
          <Badge className="bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return <Badge>{workStatus}</Badge>;
    }
  };

  // Filter quotes based on active tab and search query
  const filteredQuotes = quotes.filter(q => {
    // Status filter (for Super Admin)
    if (isSuperAdmin && activeTab !== 'all' && q.work_status !== activeTab) {
      return false;
    }
    
    // Search filter by quote number
    if (searchQuery.trim()) {
      return q.quote_no?.toLowerCase().includes(searchQuery.toLowerCase());
    }
    
    return true;
  });

  // Count by status for tabs (from all quotes, not filtered by date)
  const statusCounts = {
    all: quotes.length,
    jobsheet_pending: quotes.filter(q => q.work_status === 'jobsheet_pending').length,
    jobsheet_submitted: quotes.filter(q => q.work_status === 'jobsheet_submitted').length,
    billing_approved: quotes.filter(q => q.work_status === 'billing_approved').length,
    billing_rejected: quotes.filter(q => q.work_status === 'billing_rejected').length,
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-lg">Loading quotes...</div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="space-y-6" data-testid="jobsheet-review-page">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Jobsheet Review
          </h1>
          <p className="text-muted-foreground mt-2">
            {isSuperAdmin 
              ? 'View all jobsheets across different stages' 
              : 'Review and approve/reject submitted jobsheets'}
          </p>
        </div>

        {/* Status Filter Tabs for Super Admin */}
        {isSuperAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant={activeTab === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('all')}
              className={activeTab === 'all' ? 'bg-[#1B273D]' : ''}
            >
              All ({statusCounts.all})
            </Button>
            <Button
              variant={activeTab === 'jobsheet_pending' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('jobsheet_pending')}
              className={activeTab === 'jobsheet_pending' ? 'bg-amber-600' : ''}
            >
              <Clock className="h-3 w-3 mr-1" />
              Awaiting Upload ({statusCounts.jobsheet_pending})
            </Button>
            <Button
              variant={activeTab === 'jobsheet_submitted' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('jobsheet_submitted')}
              className={activeTab === 'jobsheet_submitted' ? 'bg-purple-600' : ''}
            >
              <FileText className="h-3 w-3 mr-1" />
              Pending Review ({statusCounts.jobsheet_submitted})
            </Button>
            <Button
              variant={activeTab === 'billing_approved' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('billing_approved')}
              className={activeTab === 'billing_approved' ? 'bg-green-600' : ''}
            >
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Approved ({statusCounts.billing_approved})
            </Button>
            <Button
              variant={activeTab === 'billing_rejected' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('billing_rejected')}
              className={activeTab === 'billing_rejected' ? 'bg-red-600' : ''}
            >
              <XCircle className="h-3 w-3 mr-1" />
              Rejected ({statusCounts.billing_rejected})
            </Button>
          </div>
        )}

        {/* Search Box */}
        <Card className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by Quote Number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-quote-input"
            />
          </div>
          {searchQuery && (
            <p className="text-sm text-muted-foreground mt-2">
              {filteredQuotes.length} quote(s) found {filteredQuotes.length !== quotes.length && `(filtered from ${quotes.length})`}
            </p>
          )}
        </Card>

        {filteredQuotes.length === 0 ? (
          <Card className="p-12 text-center glass-card">
            <p className="text-muted-foreground">
              {searchQuery 
                ? 'No quotes found matching your search'
                : isSuperAdmin && activeTab !== 'all' 
                  ? `No jobsheets with "${activeTab.replace(/_/g, ' ')}" status`
                  : 'No jobsheets pending review'}
            </p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredQuotes.map((quote) => (
              <Card key={quote.id} className="p-6">
                <div className="space-y-4">
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold font-mono">{quote.quote_no}</h3>
                        {getStatusBadge(quote.work_status)}
                      </div>
                      <p className="text-sm text-gray-600">{quote.work_description}</p>
                      <p className="text-sm text-gray-500">Supervisor: {quote.supervisor_name}</p>
                      <p className="text-sm font-medium">Amount: ₹{quote.amount_with_management_fee?.toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                  
                  {/* Document Preview Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                    {/* Job Sheet */}
                    <div className="bg-white rounded-lg border overflow-hidden">
                      <div className="p-2 bg-gray-100 border-b flex items-center gap-2">
                        <FileText className="h-4 w-4 text-[#DC143C]" />
                        <span className="text-sm font-medium">Job Sheet</span>
                      </div>
                      <div className="p-3 h-32 flex items-center justify-center bg-gray-50">
                        {quote.jobsheet_url && isImageFile(quote.jobsheet_path) ? (
                          <img 
                            src={quote.jobsheet_url} 
                            alt="Job Sheet" 
                            className="max-h-full max-w-full object-contain cursor-pointer hover:opacity-80"
                            onClick={() => openPreview(quote.jobsheet_url, 'Job Sheet')}
                          />
                        ) : quote.jobsheet_path ? (
                          <FileText className="h-12 w-12 text-red-400" />
                        ) : (
                          <span className="text-sm text-gray-400">No file</span>
                        )}
                      </div>
                      <div className="p-2 border-t flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs"
                          onClick={() => openPreview(quote.jobsheet_url, 'Job Sheet')}
                          disabled={!quote.jobsheet_url}
                        >
                          <Eye className="h-3 w-3 mr-1" /> Preview
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs"
                          onClick={() => quote.jobsheet_url && window.open(quote.jobsheet_url, '_blank')}
                          disabled={!quote.jobsheet_url}
                        >
                          <ExternalLink className="h-3 w-3 mr-1" /> Open
                        </Button>
                      </div>
                    </div>

                    {/* Before Photo */}
                    <div className="bg-white rounded-lg border overflow-hidden">
                      <div className="p-2 bg-gray-100 border-b flex items-center gap-2">
                        <Image className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium">Before Photo</span>
                      </div>
                      <div className="p-3 h-32 flex items-center justify-center bg-gray-50">
                        {quote.before_photo_url && isImageFile(quote.before_photo_path) ? (
                          <img 
                            src={quote.before_photo_url} 
                            alt="Before" 
                            className="max-h-full max-w-full object-contain cursor-pointer hover:opacity-80"
                            onClick={() => openPreview(quote.before_photo_url, 'Before Photo')}
                          />
                        ) : quote.before_photo_path ? (
                          <FileText className="h-12 w-12 text-blue-400" />
                        ) : (
                          <span className="text-sm text-gray-400">No file</span>
                        )}
                      </div>
                      <div className="p-2 border-t flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs"
                          onClick={() => openPreview(quote.before_photo_url, 'Before Photo')}
                          disabled={!quote.before_photo_url}
                        >
                          <Eye className="h-3 w-3 mr-1" /> Preview
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs"
                          onClick={() => quote.before_photo_url && window.open(quote.before_photo_url, '_blank')}
                          disabled={!quote.before_photo_url}
                        >
                          <ExternalLink className="h-3 w-3 mr-1" /> Open
                        </Button>
                      </div>
                    </div>

                    {/* After Photo */}
                    <div className="bg-white rounded-lg border overflow-hidden">
                      <div className="p-2 bg-gray-100 border-b flex items-center gap-2">
                        <Image className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium">After Photo</span>
                      </div>
                      <div className="p-3 h-32 flex items-center justify-center bg-gray-50">
                        {quote.after_photo_url && isImageFile(quote.after_photo_path) ? (
                          <img 
                            src={quote.after_photo_url} 
                            alt="After" 
                            className="max-h-full max-w-full object-contain cursor-pointer hover:opacity-80"
                            onClick={() => openPreview(quote.after_photo_url, 'After Photo')}
                          />
                        ) : quote.after_photo_path ? (
                          <FileText className="h-12 w-12 text-green-400" />
                        ) : (
                          <span className="text-sm text-gray-400">No file</span>
                        )}
                      </div>
                      <div className="p-2 border-t flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs"
                          onClick={() => openPreview(quote.after_photo_url, 'After Photo')}
                          disabled={!quote.after_photo_url}
                        >
                          <Eye className="h-3 w-3 mr-1" /> Preview
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs"
                          onClick={() => quote.after_photo_url && window.open(quote.after_photo_url, '_blank')}
                          disabled={!quote.after_photo_url}
                        >
                          <ExternalLink className="h-3 w-3 mr-1" /> Open
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons - Only show for quotes that can be acted upon */}
                  {quote.work_status === 'jobsheet_submitted' && (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleApprove(quote)}
                        disabled={submitting}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => openRejectDialog(quote)}
                        disabled={submitting}
                        variant="destructive"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  )}
                  
                  {/* Status info for non-submitted quotes */}
                  {quote.work_status === 'jobsheet_pending' && (
                    <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <span className="text-sm text-amber-800">Waiting for Site Supervisor to upload jobsheet</span>
                    </div>
                  )}
                  
                  {quote.work_status === 'billing_approved' && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-800">
                        Approved on {quote.billing_approved_at ? new Date(quote.billing_approved_at).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  )}
                  
                  {quote.work_status === 'billing_rejected' && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-200">
                      <XCircle className="h-4 w-4 text-red-600" />
                      <span className="text-sm text-red-800">
                        Rejected - Awaiting Site Supervisor resubmission
                      </span>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Reject Jobsheet/Photos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Quote: <span className="font-medium">{selectedQuote?.quote_no}</span></p>
            </div>
            
            <p className="text-sm text-gray-600">Select items to reject and provide remarks:</p>
            
            {/* Job Sheet Rejection */}
            <div className="space-y-2 p-3 border rounded-lg">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="reject_jobsheet"
                  checked={rejectJobsheet}
                  onCheckedChange={setRejectJobsheet}
                />
                <Label htmlFor="reject_jobsheet" className="font-medium">Reject Job Sheet</Label>
              </div>
              {rejectJobsheet && (
                <div className="mt-2">
                  <Label className="text-sm">
                    Remarks * <span className="text-xs text-muted-foreground">({jobsheetRemarks.length}/500)</span>
                  </Label>
                  <Textarea
                    value={jobsheetRemarks}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setJobsheetRemarks(e.target.value);
                      }
                    }}
                    maxLength={500}
                    rows={2}
                    placeholder="Enter rejection reason"
                    className="mt-1"
                  />
                </div>
              )}
            </div>
            
            {/* Before Photo Rejection */}
            <div className="space-y-2 p-3 border rounded-lg">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="reject_before"
                  checked={rejectBeforePhoto}
                  onCheckedChange={setRejectBeforePhoto}
                />
                <Label htmlFor="reject_before" className="font-medium">Reject Before Photo</Label>
              </div>
              {rejectBeforePhoto && (
                <div className="mt-2">
                  <Label className="text-sm">
                    Remarks * <span className="text-xs text-muted-foreground">({beforePhotoRemarks.length}/500)</span>
                  </Label>
                  <Textarea
                    value={beforePhotoRemarks}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setBeforePhotoRemarks(e.target.value);
                      }
                    }}
                    maxLength={500}
                    rows={2}
                    placeholder="Enter rejection reason"
                    className="mt-1"
                  />
                </div>
              )}
            </div>
            
            {/* After Photo Rejection */}
            <div className="space-y-2 p-3 border rounded-lg">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="reject_after"
                  checked={rejectAfterPhoto}
                  onCheckedChange={setRejectAfterPhoto}
                />
                <Label htmlFor="reject_after" className="font-medium">Reject After Photo</Label>
              </div>
              {rejectAfterPhoto && (
                <div className="mt-2">
                  <Label className="text-sm">
                    Remarks * <span className="text-xs text-muted-foreground">({afterPhotoRemarks.length}/500)</span>
                  </Label>
                  <Textarea
                    value={afterPhotoRemarks}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setAfterPhotoRemarks(e.target.value);
                      }
                    }}
                    maxLength={500}
                    rows={2}
                    placeholder="Enter rejection reason"
                    className="mt-1"
                  />
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={handleReject}
                disabled={submitting || (!rejectJobsheet && !rejectBeforePhoto && !rejectAfterPhoto)}
                variant="destructive"
                className="flex-1"
              >
                {submitting ? 'Processing...' : 'Confirm Rejection'}
              </Button>
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ZoomIn className="h-5 w-5" />
              {previewTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4 bg-gray-100 rounded-lg overflow-auto max-h-[70vh]">
            {previewUrl && (
              isImageFile(previewUrl) ? (
                <img 
                  src={previewUrl} 
                  alt={previewTitle}
                  className="max-w-full max-h-[65vh] object-contain"
                />
              ) : (
                <iframe
                  src={previewUrl}
                  title={previewTitle}
                  className="w-full h-[65vh] border-0"
                />
              )
            )}
          </div>
          <div className="flex justify-end">
            <a href={previewUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline">
                <ExternalLink className="h-4 w-4 mr-2" />
                Open in New Tab
              </Button>
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}
